/**
 * The Library page (W2-10 projects-data; DESIGN.md §3.1, §4.1, §5.16), routes `#/` and `#/trash`.
 *
 *  - Start cards: New chart (`project.new`), Open a chart file (`file.import`), Learn with a sample (`sample.open`);
 *    a card whose command no module registers says it is not available in this build.
 *  - "Your charts": search (title, artist, level), group by song (charts sharing a song file), sort, grid or list;
 *    the choices persist in `settings.ext['library.prefs']`. Keyboard: arrows, Enter, F2, Delete, typing searches.
 *  - Notices: the io worker's import progress (the banners modules register, e.g. the import queue's, are shown by
 *    the shell's banner row above every page since W3-01 UX-002, so the page no longer hosts them itself), and the
 *    "recovered" notice for charts whose last session did not close.
 *  - Footer: storage, persistence ("Protect my charts"), the Trash and "Download all as backup".
 *  - Dialogs of the page: New difficulty and "Open in another tab".
 *  - First run: when the first-run question has not been answered, the Library asks it (`DIALOG.firstRun`) and
 *    applies the answer (language, level, keymap preset and its editing conventions, hint-bar style, tour; see
 *    firstRun.ts).
 * The list follows other tabs (`persist.onExternalChange`).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AppServices, CommandId, IconName } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import { Button, Checkbox, EmptyState, Icon, IconButton, ProgressBar, Segmented, Select } from '../../ui';
import { firstRunHandOff } from './firstRun';
import { focusLost } from './focusHandoff';
import { useIoProgress, useNow, useSettingsValue, useText, type LibraryText } from './hooks';
import { m } from './library.messages';
import { refreshProjects } from './libraryActions';
import {
  LIBRARY_SORTS,
  matchesQuery,
  parsePrefs,
  PREFS_KEY,
  prefsToJson,
  songCounts,
  type LibraryPrefs,
  type LibrarySort,
  type LibraryView,
} from './libraryModel';
import { libraryStore, liveProjects, trashedProjects, useLibraryState, type LibraryUiState } from './libraryStore';
import { LockedElsewhere } from './LockedElsewhere';
import { NewDifficultyDialog } from './NewDifficultyDialog';
import { ProjectGrid } from './ProjectGrid';
import { RecoveryNotice } from './RecoveryNotice';
import { StorageFooter } from './StorageFooter';
import { TrashView } from './TrashView';
import s from './Library.module.css';

const SORT_TITLES: Readonly<Record<LibrarySort, MessageKey>> = {
  edited: m.sortEdited,
  opened: m.sortOpened,
  created: m.sortCreated,
  name: m.sortName,
  difficulty: m.sortDifficulty,
};

interface StartCard {
  readonly command: CommandId;
  readonly icon: IconName;
  readonly title: MessageKey;
  readonly body: MessageKey;
}

const START_CARDS: readonly StartCard[] = [
  { command: 'project.new', icon: 'Plus', title: m.startNewTitle, body: m.startNewBody },
  { command: 'file.import', icon: 'FolderOpen', title: m.startOpenTitle, body: m.startOpenBody },
  { command: 'sample.open', icon: 'GraduationCap', title: m.startSampleTitle, body: m.startSampleBody },
];

const selectProjects = (st: LibraryUiState): LibraryUiState['projects'] => st.projects;
const selectLoadError = (st: LibraryUiState): string | null => st.loadError;
const selectFocused = (st: LibraryUiState): LibraryUiState['focused'] => st.focused;
const selectRenaming = (st: LibraryUiState): LibraryUiState['renaming'] => st.renaming;
const selectBusy = (st: LibraryUiState): LibraryUiState['busy'] => st.busy;
const selectLocked = (st: LibraryUiState): LibraryUiState['locked'] => st.locked;
const selectNewDifficulty = (st: LibraryUiState): LibraryUiState['newDifficulty'] => st.newDifficulty;

/**
 * After "I'm new to charting" with no chart yet: one line saying where to start, with the sample tour one click away
 * (UX-076: the welcome promised a tour and the Library offered no next step).
 */
function NextStep(props: {
  readonly services: AppServices;
  readonly text: LibraryText;
  readonly onDismiss: () => void;
}) {
  const { services, text } = props;
  return (
    <div className={s.notice} data-tone="info" role="status" data-testid="library-next-step">
      <Icon name="GraduationCap" size={20} />
      <div className={s.noticeText}>
        <span className={s.noticeTitle}>{text.t(m.nextStepTitle)}</span>
        <span>{text.t(m.nextStepBody)}</span>
      </div>
      <Button
        variant="primary"
        icon="GraduationCap"
        onClick={() => {
          props.onDismiss();
          void services.registry.runAny('sample.open', undefined, 'button');
        }}
      >
        {text.t(m.startSampleTitle)}
      </Button>
      <IconButton icon="X" size="sm" label={{ key: m.nextStepDismiss }} onClick={props.onDismiss} />
    </div>
  );
}

function StartCards(props: {
  readonly services: AppServices;
  readonly text: LibraryText;
  /** The start card the next-step hint points at. */
  readonly highlight?: CommandId | null;
}) {
  const { services, text } = props;
  return (
    <section className={s.start} aria-label={text.t(m.startLabel)}>
      {START_CARDS.map((c) => {
        const available = services.registry.command(c.command) !== undefined;
        return (
          <button
            key={c.command}
            type="button"
            className={s.startCard}
            data-start-card
            data-highlight={props.highlight === c.command ? 'true' : undefined}
            aria-disabled={!available || undefined}
            title={available ? undefined : text.t(m.startUnavailable)}
            onClick={() => {
              if (available) void services.registry.runAny(c.command, undefined, 'button');
              else services.ui.toast({ key: m.startUnavailable }, { kind: 'info' });
            }}
          >
            <Icon name={c.icon} size={24} />
            <span className={s.startTitle}>{text.t(c.title)}</span>
            <span className={s.startBody}>{text.t(c.body)}</span>
          </button>
        );
      })}
    </section>
  );
}

/**
 * The stage of an import from the importer's progress (UX-088): it reports 0 while unpacking, 0.1 after, 0.2 once
 * the manifest is read, 0.45 after the chart, 0.6 after the files, 0.75 after converting, 1 when checked.
 */
export function importStage(p: number): MessageKey {
  if (p < 0.1) return m.importStageUnpack;
  if (p < 0.2) return m.importStageManifest;
  if (p < 0.45) return m.importStageChart;
  if (p < 0.6) return m.importStageFiles;
  if (p < 0.75) return m.importStageConvert;
  if (p < 1) return m.importStageCheck;
  return m.importStageSave;
}

function ImportProgress(props: { readonly services: AppServices; readonly text: LibraryText }) {
  const p = useIoProgress(props.services, 'import');
  if (p === null) return null;
  const stage = importStage(p.p);
  // CR-9 (UX-088): the package path the importer is on, next to the stage label ("... sample-song.wav").
  const label =
    p.file === undefined ? { key: stage } : { key: m.importStageAt, params: { stage: props.text.t(stage), file: p.file } };
  return (
    <div className={s.notice} data-tone="info" role="status">
      <Icon name="FolderInput" size={20} />
      <div className={s.noticeText}>
        <span className={s.noticeTitle}>{props.text.t(m.importing)}</span>
        {/* Unpacking reports nothing until it ends: the bar is indeterminate meanwhile. */}
        <ProgressBar value={p.p < 0.1 ? null : p.p} label={label} />
      </div>
    </div>
  );
}

function Toolbar(props: {
  readonly text: LibraryText;
  readonly prefs: LibraryPrefs;
  readonly count: number;
  readonly onPrefs: (p: LibraryPrefs) => void;
}) {
  const { text, prefs, onPrefs } = props;
  return (
    <div className={s.toolbar}>
      <h2 className={s.h2} id="library-charts-title">
        {text.t(m.yourCharts)} <span className={s.count}>{text.t(m.chartCount, { count: props.count })}</span>
      </h2>
      <div className={s.toolbarControls}>
        <Checkbox
          label={text.t(m.groupBySong)}
          checked={prefs.groupBySong}
          onChange={(v) => {
            onPrefs({ ...prefs, groupBySong: v });
          }}
        />
        <Select<LibrarySort>
          label={text.t(m.sortLabel)}
          value={prefs.sort}
          options={LIBRARY_SORTS.map((v) => ({ value: v, label: text.t(SORT_TITLES[v]) }))}
          onChange={(v) => {
            onPrefs({ ...prefs, sort: v });
          }}
        />
        <Segmented<LibraryView>
          label={text.t(m.viewLabel)}
          value={prefs.view}
          options={[
            { value: 'grid', label: text.t(m.viewGrid), icon: 'LayoutGrid' },
            { value: 'list', label: text.t(m.viewList), icon: 'List' },
          ]}
          onChange={(v) => {
            onPrefs({ ...prefs, view: v });
          }}
        />
      </div>
    </div>
  );
}

export interface LibraryRouteProps {
  readonly services: AppServices;
  readonly params: Readonly<Record<string, string>>;
  /** 'trash' for `#/trash`. */
  readonly page?: 'home' | 'trash';
}

export function LibraryRoute(props: LibraryRouteProps) {
  const { services } = props;
  const page = props.page ?? 'home';
  const text = useText(services);
  const settings = useSettingsValue(services);
  const now = useNow();
  const store = libraryStore(services);
  const projects = useLibraryState(services, selectProjects);
  const loadError = useLibraryState(services, selectLoadError);
  const focused = useLibraryState(services, selectFocused);
  const renaming = useLibraryState(services, selectRenaming);
  const busy = useLibraryState(services, selectBusy);
  const locked = useLibraryState(services, selectLocked);
  const newDifficulty = useLibraryState(services, selectNewDifficulty);
  const [query, setQuery] = useState('');
  const search = useRef<HTMLInputElement>(null);
  /** The first-run answer was "I'm new to charting": point at the sample tour until a chart exists. */
  const [nextStep, setNextStep] = useState(false);

  const prefs = useMemo(() => parsePrefs(settings.ext[PREFS_KEY]), [settings.ext]);
  const setPrefs = useCallback(
    (p: LibraryPrefs): void => {
      services.settings.setExt(PREFS_KEY, prefsToJson(p));
    },
    [services.settings],
  );

  // Mounted pages, the list (and its changes in other tabs), first run.
  useEffect(() => {
    store.update((st) => ({ mounted: st.mounted + 1 }));
    void refreshProjects(services);
    const off = services.persist.onExternalChange((e) => {
      if (e.kind === 'projects') void refreshProjects(services);
    });
    void firstRunHandOff(services).then((answer) => {
      if (answer?.level === 'guided') setNextStep(true);
    });
    return () => {
      off();
      store.update((st) => ({ mounted: Math.max(0, st.mounted - 1) }));
    };
  }, [services, store]);

  const live = useMemo(() => liveProjects(projects), [projects]);
  const showNextStep = nextStep && projects !== null && live.length === 0;
  const trashCount = useMemo(() => trashedProjects(projects).length, [projects]);
  const counts = useMemo(() => songCounts(live), [live]);
  const shown = useMemo(() => live.filter((p) => matchesQuery(p, query)), [live, query]);
  const newDifficultySource = newDifficulty === null ? undefined : live.find((p) => p.id === newDifficulty);

  // The last chart left the list (Trash, deleted) while it had focus: the keyboard goes to the first start card
  // ("New chart") instead of the page.
  const pageRef = useRef<HTMLDivElement>(null);
  const hadCharts = useRef(false);
  useLayoutEffect(() => {
    const had = hadCharts.current;
    hadCharts.current = live.length > 0;
    if (page === 'home' && had && live.length === 0 && focusLost()) {
      pageRef.current?.querySelector<HTMLElement>('[data-start-card]')?.focus();
    }
  }, [live.length, page]);

  const typeToSearch = (char: string): void => {
    setQuery((q) => q + char);
    search.current?.focus();
  };

  let list: ReactNode;
  if (projects === null) {
    list =
      loadError === null ? (
        <p className={s.muted} role="status">
          {text.t(m.loading)}
        </p>
      ) : (
        <div className={s.loadError} role="alert">
          <Icon name="CircleAlert" size={16} />
          <span>{text.t(m.loadFailed, { reason: loadError })}</span>
          <Button
            size="sm"
            icon="RefreshCw"
            onClick={() => {
              void refreshProjects(services);
            }}
          >
            {text.t(m.retry)}
          </Button>
        </div>
      );
  } else if (live.length === 0) {
    list = <EmptyState icon="LibraryBig" title={{ key: m.emptyTitle }} body={{ key: m.emptyBody }} />;
  } else if (shown.length === 0) {
    list = (
      <div className={s.noMatch}>
        <EmptyState icon="Search" title={{ key: m.noMatchTitle, params: { query } }} body={{ key: m.noMatchBody }} />
        <Button
          size="sm"
          icon="X"
          onClick={() => {
            setQuery('');
            search.current?.focus();
          }}
        >
          {text.t(m.clearSearch)}
        </Button>
      </div>
    );
  } else {
    list = (
      <ProjectGrid
        services={services}
        text={text}
        projects={shown}
        songCounts={counts}
        groupBySong={prefs.groupBySong}
        sort={prefs.sort}
        view={prefs.view}
        now={now}
        focused={focused}
        renaming={renaming}
        busy={busy}
        onTypeToSearch={typeToSearch}
      />
    );
  }

  return (
    <div ref={pageRef} className={s.page} data-region="library">
      <header className={s.header}>
        <h1 className={s.h1}>
          <Icon name="LibraryBig" size={20} />
          <span>{text.t(m.appName)}</span>
        </h1>
        {page === 'home' && (
          <label className={s.search}>
            <Icon name="Search" size={16} />
            <span className="visually-hidden">{text.t(m.searchLabel)}</span>
            <input
              ref={search}
              type="search"
              className={s.searchInput}
              placeholder={text.t(m.searchPlaceholder)}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query !== '') {
                  e.preventDefault();
                  e.stopPropagation();
                  setQuery('');
                } else if (e.key === 'ArrowDown') {
                  const first = document.querySelector<HTMLElement>(
                    '[data-region="library"] [data-card-main][tabindex="0"]',
                  );
                  if (first !== null) {
                    e.preventDefault();
                    first.focus();
                  }
                }
              }}
            />
          </label>
        )}
        <span className={s.headerActions}>
          <IconButton icon="CircleHelp" label={{ key: m.help }} command="help.open" />
          <IconButton icon="Settings" label={{ key: m.settings }} command="settings.open" />
        </span>
      </header>

      <main className={s.main}>
        {page === 'trash' ? (
          <TrashView services={services} />
        ) : (
          <>
            <StartCards services={services} text={text} highlight={showNextStep ? 'sample.open' : null} />
            {showNextStep && (
              <NextStep
                services={services}
                text={text}
                onDismiss={() => {
                  setNextStep(false);
                }}
              />
            )}
            <p className={s.dropHint}>{text.t(m.dropHint)}</p>
            <ImportProgress services={services} text={text} />
            <RecoveryNotice services={services} text={text} projects={live} />
            <section className={s.charts} aria-labelledby="library-charts-title">
              <Toolbar text={text} prefs={prefs} count={live.length} onPrefs={setPrefs} />
              {list}
            </section>
          </>
        )}
      </main>

      <StorageFooter services={services} trashCount={trashCount} liveCount={live.length} page={page} />

      {locked !== null && (
        <LockedElsewhere
          services={services}
          request={locked}
          onClose={() => {
            store.update({ locked: null });
          }}
        />
      )}
      {newDifficultySource !== undefined && (
        <NewDifficultyDialog
          services={services}
          source={newDifficultySource}
          projects={live}
          onClose={() => {
            store.update({ newDifficulty: null });
          }}
        />
      )}
    </div>
  );
}

/** Route component of `#/trash`. */
export function TrashRoute(props: Omit<LibraryRouteProps, 'page'>) {
  return <LibraryRoute {...props} page="trash" />;
}
